import NextButton from 'components/common/NextButton'
import PrevButton from 'components/common/PrevButton'
import ProductCard from 'components/product/ProductCard'
import ProductModal from 'components/product/ProductCard/ProductModal'
import useCart from 'hooks/useCart'
import useSlug from 'hooks/useSlug'
import { useKeenSlider } from 'keen-slider/react'
import { isAbsent, isEmptyArray, isPresent } from 'lib/utils'

import { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  IProduct,
  selectCategoryList,
  selectProductList,
} from 'store/api'
import { selectIsBrowser } from 'store/ui'

const RecommendedProducts = () => {
  const { queryLng } = useSlug()

  const onOrderButtonClick = useCallback((product: IProduct) => {
    setProduct(product)
    setIsProductModalOpen(true)
  }, [])

  const closeProductModal = useCallback(() => {
    setProduct(null)
    setIsProductModalOpen(false)
  }, [])

  let [product, setProduct] = useState<IProduct | null>(null)
  let [isProductModalOpen, setIsProductModalOpen] = useState(false)

  const { items } = useCart()
  const productList = useSelector(selectProductList(queryLng))

  const recommendedCategory = '26'

  const categoryList = useSelector(selectCategoryList(queryLng))

  const allProducts = useMemo(() => {
    const _temp: any = []
    categoryList?.forEach((c) => {
      const products = productList[c.id.toString()]
      if (products !== undefined)
        _temp.push(
          products.map((p) => ({
            category: c.slug,
            ...p,
          }))
        )
    })
    return _temp.flat()
  }, [categoryList, productList])

  const productsByCategory = useMemo(() => {
    return (
      productList[recommendedCategory]?.filter((p) => {
        return allProducts.find((i: any) => i.id === p.id)
      })?.map((p) => {
        const categorySlug =
          allProducts.find((i: any) => i.id === p.id)?.category ??
          'recommended'
        return {
          ...p,
          category: categorySlug,
        }
      }) ?? []
    )
  }, [recommendedCategory, productList, allProducts])

  let isBrowser = useSelector(selectIsBrowser)

  const [currentSlide, setCurrentSlide] = useState(0)
  const [maxIndex, setMaxIndex] = useState(0)

  let [ref, slider] = useKeenSlider<HTMLDivElement>({
    slides: {
      perView: 1.3,
      spacing: 8,
    },
    breakpoints: {
      '(min-width: 	640px)': {
        drag: false,
        slides: { perView: 2.2, spacing: 8 },
      },
      '(min-width: 768px)': {
        slides: {
          perView: 3.2,
          spacing: 8,
        },
      },
      '(min-width: 1024px)': {
        slides: {
          perView: 4.2,
          spacing: 8,
        },
      },
      '(min-width: 1500px)': {
        slides: {
          perView: 5.2,
          spacing: 8,
        },
      },
    },
    created(slider) {
      setMaxIndex(slider.track.details.maxIdx)
    },
    slideChanged(slider) {
      setCurrentSlide(slider.track.details.rel)
      setMaxIndex(slider.track.details.maxIdx)
    },
  })

  const renderItem = useCallback(
    (p: IProduct) => {
      const _item = items.find((i) => i.product.id === p.id)
      const isSimpleProduct =
        (p.type === 'simple' || p.variations_count == 1) &&
        isAbsent(p.options?.ingsgroups)

      const quantity = isPresent(_item)
        ? isSimpleProduct
          ? _item.quantity
          : items.reduce((count, i) => {
              if (i.product.id === p.id) count += i.quantity
              return count
            }, 0)
        : 0

      return (
        <div
          className='flex-grow group keen-slider__slide gap-2'
          key={p.id}
        >
          {isBrowser && (
            <ProductCard
              {...p}
              descClassName='font-light'
              className='lg:border lg:border-grey'
              cb={() => onOrderButtonClick(p)}
              categorySlug={p.category}
              items={items}
              quantity={quantity}
            />
          )}
        </div>
      )
    },
    [isBrowser, items, onOrderButtonClick]
  )

  if (
    isAbsent(productsByCategory) ||
    isEmptyArray(productsByCategory)
  )
    return null

  return (
    <div className='bg-transparent mb-12'>
      <div className='flex items-center justify-between z-10 mb-4'>
        <div className='text-xs lg:text-base font-bold uppercase font-unbounded leading-tight'>
          Не забудьте добавить к заказу:
        </div>

        <div className='hidden md:flex gap-4 justify-end flex-1 pr-4 xl:pr-12'>
          <PrevButton
            active={currentSlide > 0}
            onClick={() => slider.current?.prev()}
          />
          <NextButton
            active={currentSlide < maxIndex}
            onClick={() => slider.current?.next()}
          />
        </div>
      </div>

      <div className='keen-slider z-10' ref={ref}>
        {productsByCategory.map((p) => renderItem(p))}
      </div>

      <ProductModal
        isProductModalOpen={isProductModalOpen}
        closeProductModal={closeProductModal}
        product={product}
      />
    </div>
  )
}

export default RecommendedProducts
